'use client';
import React from 'react';
import { Box, Stack, TextInput, Text, Anchor, Alert, Divider, Button, Group } from '@mantine/core';

import { useSearchParams } from 'next/navigation';
import Image from 'next/image';
import Link from 'next/link';
import ServerActionSubmitButton from '../../../components/ServerActionSubmitButton/ServerActionSubmitButton';
import { useFormState } from 'react-dom';
import { authenticate, sendCode } from './actions';
import { isEmpty } from 'lodash';
import logo from '../../../../public/shepherd-logo-black.png';
import { IconLogin, IconRefresh, IconSend } from '@tabler/icons-react';

const SendCodeForm = () => {
  const [state, dispatch] = useFormState(sendCode, undefined);
  return (
    <form style={{ width: '100%' }} action={dispatch}>
      <Box p="lg" maw="400">
        <Stack p="lg" gap="xs" align="center">
          <Image src={logo} alt="logo" priority height={50} />
          <Text fw={400} size="lg" c="dark.3">
            Log In
          </Text>
        </Stack>
        <Stack gap="lg">
          <Stack gap="xs">
            <Box>
              <TextInput
                name="email"
                type="email"
                required
                withAsterisk={false}
                size="md"
                label="Email"
                placeholder="builder@withshepherd.com"
                error={!isEmpty(state?.error?._errors) && state?.error?._errors}
              />
            </Box>
            <ServerActionSubmitButton icon={<IconSend size="14" />}>
              Send Code
            </ServerActionSubmitButton>
          </Stack>
          <Divider />
          <Text fw={400} size="md" c="dark.3">
            Don&apos;t have a log in? Learn more by reaching out to{' '}
            <Anchor c="orange" href="mailto:support@withshepherd.com">
              support@withshepherd.com
            </Anchor>
            .
          </Text>
        </Stack>
      </Box>
    </form>
  );
};

const EnterCodeForm = ({ email }: { email: string }) => {
  const [state, dispatch] = useFormState(authenticate, undefined);
  return (
    <form style={{ width: '100%' }} action={dispatch}>
      <Box p="lg" maw="400">
        <Stack p="lg" gap="xs" align="center">
          <Image src={logo} alt="logo" priority height={50} />
          <Text fw={400} size="lg" c="dark.3">
            Log In
          </Text>
        </Stack>
        <Stack gap="xs">
          <TextInput
            name="email"
            type="email"
            required
            size="md"
            label="Email"
            withAsterisk={false}
            value={email}
            placeholder="builder@withshepherd.com"
          />
          <TextInput
            data-testid="SignIn:EnterCode"
            required
            withAsterisk={false}
            size="md"
            label="Verification Code"
            name="code"
          />
          {!isEmpty(state?.error?._errors) && (
            <Alert color="red" title="Error">
              {state?.error?._errors}
            </Alert>
          )}
          {state?.message && (
            <Alert title="Error" color="orange">
              {state?.message}
            </Alert>
          )}
          <ServerActionSubmitButton icon={<IconLogin size="14" />}>Log In</ServerActionSubmitButton>
        </Stack>
        <Group>
          <Button
            ml="auto"
            size="xs"
            variant="transparent"
            color="orange"
            component={Link}
            href={'/auth/sign-in'}
            leftSection={<IconRefresh size="14" />}
          >
            Reset
          </Button>
        </Group>
        <Divider />
        <Text fw={400} size="md" c="dark.3">
          Having trouble? Reach out to{' '}
          <Anchor c="orange" href="mailto:support@withshepherd.com">
            support@withshepherd.com
          </Anchor>
          .
        </Text>
      </Box>
    </form>
  );
};

const SignIn = () => {
  const params = useSearchParams();

  const email = params?.get('email');

  if (email) {
    return <EnterCodeForm email={email} />;
  }

  return <SendCodeForm />;
};

export default SignIn;
